import { useCallback, useState } from 'react';
import { Button, Indicator } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { IconNotes } from '@tabler/icons-react';

import { isNotesEmpty } from '../../utils/notes.js';

interface NotesFormValues {
  notes?: string | null;
}

interface NotesBadgeProps {
  form: UseFormReturnType<NotesFormValues>;
  onClick: () => void;
}

/**
 * Isolierte Blatt-Komponente (D-05): abonniert das notes-Feld selbst per form.watch, statt
 * hasNotes aus geliftetem Parent-State zu lesen. Beim Tippen in den Notizen rendert dadurch
 * nur dieser Badge neu -- nie der Parent.
 *
 * Optik 1:1 aus ReportEditPage.tsx (Indicator um einen Button mit IconNotes), keine
 * Memoisierungs-Kruecke noetig -- die Isolation passiert bereits durch das eigene Abo.
 */
export function NotesBadge({ form, onClick }: NotesBadgeProps) {
  const [notes, setNotes] = useState<string | null>(() => form.getValues().notes ?? null);

  // Stabiler Handler: Mantines interner watch-Effect haengt am Dep-Array [callback] (siehe
  // use-form-watch.mjs) -- ein bei jedem Render neu erzeugter Inline-Arrow wuerde den
  // Watcher bei jedem Render ab- und wieder anmelden.
  const handleNotesChange = useCallback(({ value }: { value: string | null | undefined }) => {
    setNotes(value ?? null);
  }, []);

  // form.watch ruft intern selbst useEffect auf und meldet den Watcher im Effect-Cleanup
  // wieder ab (Quellcode-verifiziert, 18-RESEARCH.md A1 widerlegt: watch gibt KEINE
  // Unsubscribe-Funktion zurueck). Der Aufruf gehoert deshalb direkt in den Render-Body --
  // ein eigenes useEffect drumherum waere ein Hook-Aufruf in einem Callback und stuerzt zur
  // Laufzeit mit "Invalid hook call" ab. NICHT zu einem useEffect "reparieren".
  form.watch('notes', handleNotesChange);

  const hasNotes = !isNotesEmpty(notes);

  return (
    <Indicator color="vdzYellow.6" size={16} offset={4} disabled={!hasNotes} withBorder>
      <Button variant="default" leftSection={<IconNotes size={16} />} onClick={onClick}>
        Notizen
      </Button>
    </Indicator>
  );
}
